import { createApp } from "vue";
import { createPinia } from "pinia";
import {
  VueQueryPlugin,
  QueryClient,
  QueryCache,
  MutationCache,
} from "@tanstack/vue-query";
import { MotionPlugin } from "@vueuse/motion";
import type { AxiosError } from "axios";
import router from "@/router";
import { useUIStore } from "@/stores/ui";
import { useAuthStore } from "@/stores/auth";
import { extractApiError } from "@/utils/apiError";
import "./style.css";
import App from "./App.vue";
import * as Sentry from "@sentry/vue";

const app = createApp(App);

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN || "",
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/sora-recruit\.web\.id\/api/,
  ],
  replaysSessionSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE,
});
const pinia = createPinia();
app.use(pinia);

// [W1-01] Initialize auth early but don't await here to allow app shell to render.
// The router guard will await this if navigation to a protected route occurs.
const authStore = useAuthStore(pinia);
authStore.initAuth();

// Statuses already toasted by the Axios response interceptor — skip to avoid double-toast.
function isInterceptorHandled(err: unknown): boolean {
  const status = (err as AxiosError)?.response?.status;
  const code = (err as AxiosError<{ error?: { code?: string } }>)?.response
    ?.data?.error?.code as string | undefined;
  const hasNoResponse = !(err as AxiosError)?.response;
  // Suppress validation errors from CV apply — handled by applyParsedData summary
  if (
    status !== undefined &&
    (code === "VALIDATION_ERROR" || code === "SKILL_ALREADY_EXISTS")
  ) {
    return true;
  }
  return (
    hasNoResponse ||
    status === 429 ||
    status === 403 ||
    (status !== undefined && status >= 500)
  );
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (err) => {
      if (isInterceptorHandled(err)) return;
      const ui = useUIStore(pinia);
      ui.error(
        "Kesalahan Terjadi",
        extractApiError(err, "Gagal mengambil data dari server"),
      );
    },
  }),
  mutationCache: new MutationCache({
    onError: (err) => {
      if (isInterceptorHandled(err)) return;
      const ui = useUIStore(pinia);
      ui.error(
        "Aksi Gagal",
        extractApiError(err, "Terjadi kesalahan saat menyimpan data"),
      );
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

app.use(VueQueryPlugin, { queryClient });
app.use(router);
app.use(MotionPlugin);

app.mount("#app");
